import React from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import { RARITY_ORDER, getRarity } from '../../features/scrabble/config/rarityConfig';
import { SHOP_KIND_LABELS } from '../../features/scrabble/config/shopKinds';
import { formatInt } from '../../utils/format';
import WidgetShell from './WidgetShell';


function ShopTendenciesWidget({ stats }) {
  const shopKindEntries = React.useMemo(() => {
    const byKind = stats?.economy?.shopSelectionsByKind || {};
    return Object.entries(SHOP_KIND_LABELS)
      .map(([kind, label]) => ({ kind, label, count: Number(byKind[kind]) || 0 }))
      .filter((entry) => entry.count > 0);
  }, [stats?.economy?.shopSelectionsByKind]);

  const shopRarityEntries = React.useMemo(() => {
    const byRarity = stats?.economy?.shopSelectionsByRarity || {};
    return RARITY_ORDER.map((rarity) => ({ rarity, count: Number(byRarity[rarity]) || 0 })).filter(
      (entry) => entry.count > 0
    );
  }, [stats?.economy?.shopSelectionsByRarity]);

  const totalShopKindSelections = React.useMemo(
    () => shopKindEntries.reduce((a, b) => a + b.count, 0),
    [shopKindEntries]
  );

  const totalShopRaritySelections = React.useMemo(
    () => shopRarityEntries.reduce((a, b) => a + b.count, 0),
    [shopRarityEntries]
  );

  return (
    <WidgetShell
      icon={<StorefrontRoundedIcon color="primary" fontSize="small" />}
      title="Shop Tendencies"
    >
      {shopKindEntries.length > 0 || shopRarityEntries.length > 0 ? (
        <Stack spacing={2}>
          {shopKindEntries.length > 0 && (
            <Box>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 0.9, color: 'text.secondary', fontSize: 10 }}
              >
                By Type
              </Typography>
              <Stack spacing={0.9} sx={{ mt: 0.5 }}>
                {shopKindEntries.map(({ kind, label, count }) => {
                  const pct =
                    totalShopKindSelections > 0 ? (count / totalShopKindSelections) * 100 : 0;
                  return (
                    <Stack key={kind} spacing={0.4}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatInt(count)} ({Math.round(pct)}%)
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, pct))}
                        sx={{ height: 7, borderRadius: 6 }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}

          {shopRarityEntries.length > 0 && (
            <Box>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 0.9, color: 'text.secondary', fontSize: 10 }}
              >
                By Rarity
              </Typography>
              <Stack spacing={0.9} sx={{ mt: 0.5 }}>
                {shopRarityEntries.map(({ rarity, count }) => {
                  const cfg = getRarity(rarity);
                  const pct =
                    totalShopRaritySelections > 0
                      ? (count / totalShopRaritySelections) * 100
                      : 0;
                  return (
                    <Stack key={rarity} spacing={0.4}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: cfg.color,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {cfg.label}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {formatInt(count)} ({Math.round(pct)}%)
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, pct))}
                        sx={{
                          height: 7,
                          borderRadius: 6,
                          backgroundColor: (theme) => alpha(cfg.color, 0.15),
                          '& .MuiLinearProgress-bar': { backgroundColor: cfg.color },
                        }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Stack>
      ) : (
        <Typography color="text.secondary" variant="body2">
          No shop selection data yet. Start buying upgrades to see your tendencies.
        </Typography>
      )}
    </WidgetShell>
  );
}

export default ShopTendenciesWidget;
